import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, LayoutGrid, Layers, Bell, Activity, Lightbulb, List, LayoutList, ArrowUp, ArrowDown } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getComparatifs,
  createComparatif,
  updateComparatif,
  deleteComparatif } from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import {
  EMPTY_COMPARATIFS_FORM,
  buildComparatifsPayload,
  recordToFormData } from './12-comparatifs-evolution/comparatifsPayload';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { CustomDatePicker } from '../../shared/components/custom';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import '../../core/styles/global-dropdowns.css';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';

const COMPARATIFS_LIST_COLUMNS = [
  { key: 'ref', labelKey: 'col_reference' },
  { key: 'titre', labelKey: 'col_title' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'exercice', labelKey: 'col_exercise' },
  { key: 'trend', labelKey: 'col_trend' },
  { key: 'status', labelKey: 'col_status' },
];

const ComparatifsEvolutionPage = () => {
  const { t } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.comparatifsEvolutionPage.${key}`, opts);

  const translateStatus = (value) => {
    const map = {
      'Brouillon': p('status_draft'),
      'Finalisé': p('status_finalized'),
      'Validé': p('status_validated'),
      'Diffusé': p('status_distributed') };
    return map[value] || value;
  };

  const translateTrend = (value) => {
    const map = {
      'Hausse': p('trend_rise'),
      'Baisse': p('trend_fall'),
      'Stable': p('trend_stable') };
    return map[value] || value || p('stable');
  };

  const translateType = (value) => {
    const map = {
      'Exercice': p('type_exercise'),
      'Budget': p('type_budget'),
      'Prévisions': p('type_forecast'),
      'Secteur': p('type_sector'),
      'Historique': p('type_historical') };
    return map[value] || value;
  };

  const translateCategory = (value) => {
    const map = {
      'Bilan': p('cat_balance'),
      'Résultat': p('cat_result'),
      'Trésorerie': p('cat_treasury'),
      'Ratios': p('cat_ratios'),
      'Global': p('cat_global') };
    return map[value] || value;
  };

  const translateGranularity = (value) => {
    const map = {
      'Mensuelle': p('granularity_monthly'),
      'Trimestrielle': p('granularity_quarterly'),
      'Annuelle': p('granularity_annual') };
    return map[value] || value || p('na');
  };

  const typeSelectOptions = useMemo(() => [
    { value: 'Exercice', label: p('type_exercise'), isDefault: true },
    { value: 'Budget', label: p('type_budget'), isDefault: true },
    { value: 'Prévisions', label: p('type_forecast'), isDefault: true },
    { value: 'Secteur', label: p('type_sector'), isDefault: true },
    { value: 'Historique', label: p('type_historical'), isDefault: true },
  ], [t]);

  const categorySelectOptions = useMemo(() => [
    { value: 'Bilan', label: p('cat_balance'), isDefault: true },
    { value: 'Résultat', label: p('cat_result'), isDefault: true },
    { value: 'Trésorerie', label: p('cat_treasury'), isDefault: true },
    { value: 'Ratios', label: p('cat_ratios'), isDefault: true },
    { value: 'Global', label: p('cat_global'), isDefault: true },
  ], [t]);

  const granularitySelectOptions = useMemo(() => [
    { value: 'Mensuelle', label: p('granularity_monthly'), isDefault: true },
    { value: 'Trimestrielle', label: p('granularity_quarterly'), isDefault: true },
    { value: 'Annuelle', label: p('granularity_annual'), isDefault: true },
  ], [t]);

  const statusSelectOptions = useMemo(() => [
    { value: 'Brouillon', label: p('status_draft'), isDefault: true },
    { value: 'Finalisé', label: p('status_finalized'), isDefault: true },
    { value: 'Validé', label: p('status_validated'), isDefault: true },
    { value: 'Diffusé', label: p('status_distributed'), isDefault: true },
  ], [t]);

  const confidentialitySelectOptions = useMemo(() => [
    { value: 'Public', label: p('conf_public'), isDefault: true },
    { value: 'Interne', label: p('conf_internal'), isDefault: true },
    { value: 'Confidentiel', label: p('conf_confidential'), isDefault: true },
    { value: 'Strictement Confidentiel', label: p('conf_strictly_confidential'), isDefault: true },
  ], [t]);

  const [searchTerm, setSearchTerm] = useState('');
  const [comparatifs, setComparatifs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [comparatifsEvolutionFormTab, setComparatifsEvolutionFormTab] = useState('comparatifInfo');
  const comparatifsEvolutionFormTabs = useMemo(() => ([
    { id: 'comparatifInfo', label: p('section_comparatif_info'), icon: ChevronUp },
    { id: 'analyseRecommandations', label: p('section_analysis_recommendations'), icon: ChevronUp },
  ]), [p]);

  
  const [formData, setFormData] = useState({ ...EMPTY_COMPARATIFS_FORM });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [setDateRange] = useState({ start: null, end: null });
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_comparatifs');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('ref', (row) => row.comparatif_ref ?? ''),
    textColumnRule('titre', (row) => row.titre_comparatif ?? ''),
    textColumnRule('type', (row) => row.type_comparatif ?? ''),
    textColumnRule('exercice', (row) => row.exercice_fiscal ?? ''),
  ], []);

  useEffect(() => {
    loadComparatifs();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadComparatifs = async () => {
    try {
      const data = await getComparatifs();
      setComparatifs(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading comparatifs:', error);
      setComparatifs([]);
    }
  };

  const stats = {
    total: comparatifs.length,
    positive: comparatifs.filter(c => c.tendances_direction === 'Hausse').length,
    negative: comparatifs.filter(c => c.tendances_direction === 'Baisse').length,
    significantGaps: comparatifs.filter(c => Math.abs(parseFloat(c.variance_favorable || 0) - parseFloat(c.variance_defavorable || 0)) > 10000).length,
    alerts: comparatifs.reduce((sum, c) => {
      try {
        const alertes = JSON.parse(c.alertes_tendances || '[]');
        return sum + (Array.isArray(alertes) ? alertes.length : 0);
      } catch {
        return sum;
      }
    }, 0)
  };

  const mainTabs = [
    { id: 'all_comparatifs', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'by_type', nameKey: 'tab_by_type', icon: Activity },
    { id: 'by_category', nameKey: 'tab_by_category', icon: TrendingUp },
    { id: 'by_status', nameKey: 'tab_by_status', icon: CheckCircle }
  ];

  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'Hausse', nameKey: 'filter_positive', icon: ArrowUp },
    { id: 'Baisse', nameKey: 'filter_negative', icon: ArrowDown },
    { id: 'alerts', nameKey: 'filter_with_alerts', icon: Bell }
  ];

  const statCards = [
    { labelKey: 'stat_total', value: stats.total, icon: TrendingUp, color: 'from-teal-500 to-teal-600' },
    { labelKey: 'stat_positive', value: stats.positive, icon: ArrowUp, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_negative', value: stats.negative, icon: ArrowDown, color: 'from-red-500 to-red-600' },
    { labelKey: 'stat_significant_gaps', value: stats.significantGaps, icon: AlertCircle, color: 'from-amber-500 to-amber-600' },
    { labelKey: 'stat_alerts', value: stats.alerts, icon: Bell, color: 'from-cyan-500 to-cyan-600' }
  ];

  const comparatifsDistribution = {
    all_comparatifs: {
      titleKey: 'dist_overview',
      data: [
        { nameKey: 'dist_rise', filterKey: 'Hausse', count: stats.positive, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_fall', filterKey: 'Baisse', count: stats.negative, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { nameKey: 'dist_significant_gaps', filterKey: '__gaps__', count: stats.significantGaps, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { nameKey: 'dist_active_alerts', filterKey: '__alerts__', count: stats.alerts, color: 'bg-gradient-to-r from-cyan-500 to-cyan-600' }
      ]
    },
    by_type: {
      titleKey: 'dist_by_type',
      data: [
        { nameKey: 'dist_type_exercise', filterKey: 'Exercice', count: comparatifs.filter(c => c.type_comparatif === 'Exercice').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_type_budget', filterKey: 'Budget', count: comparatifs.filter(c => c.type_comparatif === 'Budget').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_type_forecast', filterKey: 'Prévisions', count: comparatifs.filter(c => c.type_comparatif === 'Prévisions').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { nameKey: 'dist_type_sector', filterKey: 'Secteur', count: comparatifs.filter(c => c.type_comparatif === 'Secteur').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
      ]
    },
    by_category: {
      titleKey: 'dist_by_category',
      data: [
        { nameKey: 'dist_cat_balance', filterKey: 'Bilan', count: comparatifs.filter(c => c.categorie_analyse === 'Bilan').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_cat_result', filterKey: 'Résultat', count: comparatifs.filter(c => c.categorie_analyse === 'Résultat').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_cat_treasury', filterKey: 'Trésorerie', count: comparatifs.filter(c => c.categorie_analyse === 'Trésorerie').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { nameKey: 'dist_cat_ratios', filterKey: 'Ratios', count: comparatifs.filter(c => c.categorie_analyse === 'Ratios').length, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    },
    by_status: {
      titleKey: 'dist_by_status',
      data: [
        { nameKey: 'dist_status_draft', filterKey: 'Brouillon', count: comparatifs.filter(c => c.statut_analyse === 'Brouillon').length, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { nameKey: 'dist_status_finalized', filterKey: 'Finalisé', count: comparatifs.filter(c => c.statut_analyse === 'Finalisé').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_status_validated', filterKey: 'Validé', count: comparatifs.filter(c => c.statut_analyse === 'Validé').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_status_distributed', filterKey: 'Diffusé', count: comparatifs.filter(c => c.statut_analyse === 'Diffusé').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' }
      ]
    }
  };

  const distributionInfo = comparatifsDistribution[mainViewMode] || comparatifsDistribution.all_comparatifs;

  const filteredComparatifs = useMemo(() => {
    const base = comparatifs.filter(c => {

      if (searchTerm && !c.comparatif_ref?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !c.titre_comparatif?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      if (quickFilter === 'Hausse' && c.tendances_direction !== 'Hausse') return false;
      if (quickFilter === 'Baisse' && c.tendances_direction !== 'Baisse') return false;
      if (quickFilter === 'alerts') {
        try {
          const alertes = JSON.parse(c.alertes_tendances || '[]');
          if (!Array.isArray(alertes) || alertes.length === 0) return false;
        } catch {
          return false;
        }
      }

      if (activeDistributionFilter) {
        if (activeDistributionFilter === '__gaps__') {
          if (Math.abs(parseFloat(c.variance_favorable || 0) - parseFloat(c.variance_defavorable || 0)) <= 10000) return false;
        } else if (activeDistributionFilter === '__alerts__') {
          try {
            const alertes = JSON.parse(c.alertes_tendances || '[]');
            if (!Array.isArray(alertes) || alertes.length === 0) return false;
          } catch {
            return false;
          }
        } else if (mainViewMode === 'by_type' && c.type_comparatif !== activeDistributionFilter) return false;
        else if (mainViewMode === 'by_category' && c.categorie_analyse !== activeDistributionFilter) return false;
        else if (mainViewMode === 'by_status' && c.statut_analyse !== activeDistributionFilter) return false;
        else if (mainViewMode === 'all_comparatifs') {
          if (activeDistributionFilter === 'Hausse' && c.tendances_direction !== 'Hausse') return false;
          if (activeDistributionFilter === 'Baisse' && c.tendances_direction !== 'Baisse') return false;
        }
      }

      return true;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [comparatifs, searchTerm, quickFilter, activeDistributionFilter, mainViewMode, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredComparatifs, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(COMPARATIFS_LIST_COLUMNS, p);
  const comparatifsTableColSpan = visibleDataColumnCount + 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = buildComparatifsPayload(formData, { forUpdate: Boolean(editingRecord) });
      if (editingRecord) {
        await updateComparatif(editingRecord.id, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'المقارنة' }));
      } else {
        await createComparatif(submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'المقارنة' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, error.message || p('alert_save_error'));
    } finally {
      resetForm();
      loadComparatifs();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData(recordToFormData(record));
    setComparatifsEvolutionFormTab('comparatifInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        await deleteComparatif(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'المقارنة' }));
        loadComparatifs();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteComparatif(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'المقارنات' }));
        setSelectedRecords([]);
        clearSelection();
        loadComparatifs();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_COMPARATIFS_FORM });
    setEditingRecord(null);
    setShowModal(false);
    setComparatifsEvolutionFormTab('comparatifInfo');
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData({
      ...EMPTY_COMPARATIFS_FORM,
      exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    });
    setComparatifsEvolutionFormTab('comparatifInfo');
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Brouillon': { style: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Edit, label: p('status_draft') },
      'Finalisé': { style: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle, label: p('status_finalized') },
      'Validé': { style: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, label: p('status_validated') },
      'Diffusé': { style: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: CheckCircle, label: p('status_distributed') }
    };
    const config = statusConfig[status] || statusConfig['Brouillon'];
    const Icon = config.icon;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.style}`}><Icon size={14} />{config.label}</span>;
  };

  const getTendanceIcon = (direction) => {
    if (direction === 'Hausse') return <ArrowUp size={14} className="text-green-400" />;
    if (direction === 'Baisse') return <ArrowDown size={14} className="text-red-400" />;
    return <Activity size={14} className="text-gray-400" />;
  };

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-teal-400" />
            {p('title')}
            <button onClick={loadComparatifs} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} 
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-teal-500 to-teal-700 border-teal-400 shadow-lg shadow-teal-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button 
            className="bg-gradient-to-r from-teal-500 to-teal-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-600 hover:to-teal-800 transition-all duration-300 flex items-center space-x-2" 
            onClick={openCreateModal}
          >
            <Plus className="h-5 w-5" />
            <span>{p('add_button')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button 
                  key={tab.id} 
                  onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} 
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-teal-500 to-teal-700 text-white shadow-lg shadow-teal-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          
          {mainViewMode === 'all_comparatifs' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button 
                    key={filter.id} 
                    onClick={() => setQuickFilter(filter.id)} 
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-teal-500 to-teal-700 text-white shadow-lg shadow-teal-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <X size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button 
                  key={item.nameKey} 
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} 
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-teal-400/60 bg-teal-500/20 shadow-lg shadow-teal-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{p(item.nameKey)}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      return p('percent_of_total', { percent: pct });
                    })()}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('ref') ? (
                    <TableColumnHeaderFilter columnKey="ref" label={p('col_reference')} value={columnFilters.ref || ''} onChange={(v) => setColumnFilter('ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('titre') ? (
                    <TableColumnHeaderFilter columnKey="titre" label={p('col_title')} value={columnFilters.titre || ''} onChange={(v) => setColumnFilter('titre', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('type') ? (
                    <TableColumnHeaderFilter columnKey="type" label={p('col_type')} value={columnFilters.type || ''} onChange={(v) => setColumnFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('exercice') ? (
                    <TableColumnHeaderFilter columnKey="exercice" label={p('col_exercise')} value={columnFilters.exercice || ''} onChange={(v) => setColumnFilter('exercice', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('trend') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_trend')}</th>
                  ) : null}
                  {isColVisible('status') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredComparatifs.length > 0 ? (
                  filteredComparatifs.map((record, index) => (
                    <tr 
                      key={record.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {isColVisible('ref') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{record.comparatif_ref}</span>
                      </td>
                      ) : null}
                      {isColVisible('titre') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.titre_comparatif}</span>
                          <span className="text-white/50 text-xs mt-0.5">{translateCategory(record.categorie_analyse) || p('na')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">{translateType(record.type_comparatif) || p('na')}</span>
                      </td>
                      ) : null}
                      {isColVisible('exercice') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">{record.exercice_fiscal || p('na')}</span>
                      </td>
                      ) : null}
                      {isColVisible('trend') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center items-center gap-1">
                          {getTendanceIcon(record.tendances_direction)}
                          <span className="text-white/70 text-sm">{translateTrend(record.tendances_direction)}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          {getStatusBadge(record.statut_analyse || 'Brouillon')}
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                          />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={comparatifsTableColSpan} className="text-center py-12 text-white/60">
                      <TrendingUp size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredComparatifs.length > 0 ? (
              filteredComparatifs.map((record, index) => (
                <motion.div 
                  key={record.id} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: index * 0.05 }} 
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <TrendingUp size={20} className="text-teal-400" />
                          {record.titre_comparatif}
                        </h3>
                        <div className="flex">{getStatusBadge(record.statut_analyse || 'Brouillon')}</div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_general_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_ref')}:</span> {record.comparatif_ref}</div>
                            <div><span className="font-medium">{p('card_type')}:</span> {translateType(record.type_comparatif)}</div>
                            <div><span className="font-medium">{p('card_category')}:</span> {translateCategory(record.categorie_analyse)}</div>
                            <div><span className="font-medium">{p('card_exercise')}:</span> {record.exercice_fiscal}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_trends')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-1">
                              {getTendanceIcon(record.tendances_direction)}
                              <span className="font-medium">{p('card_direction')}:</span> {translateTrend(record.tendances_direction)}
                            </div>
                            <div><span className="font-medium">{p('card_granularity')}:</span> {translateGranularity(record.granularite)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_controller')}:</span> {record.validation_controleur ? p('validated') : p('pending')}</div>
                            <div><span className="font-medium">{p('card_direction_validation')}:</span> {record.validation_direction ? p('validated') : p('pending')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <TrendingUp size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowViewModal(false)}
          role="presentation"
        >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" 
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-teal-400" />{p('section_general_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('label_reference')}</p><p className="text-white font-medium">{viewingRecord.comparatif_ref}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_title')}</p><p className="text-white font-medium">{viewingRecord.titre_comparatif}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_type')}</p><p className="text-white font-medium">{translateType(viewingRecord.type_comparatif)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_category')}</p><p className="text-white font-medium">{translateCategory(viewingRecord.categorie_analyse)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_fiscal_year')}</p><p className="text-white font-medium">{viewingRecord.exercice_fiscal}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_status')}</p>{getStatusBadge(viewingRecord.statut_analyse || 'Brouillon')}</div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4">{p('section_description')}</h3>
                  <p className="text-white/70 text-sm">{viewingRecord.description_comparatif || p('no_description')}</p>
                </div>
              </div>
            </motion.div>
        </div>,
        document.body
      )}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="comparatif-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-teal-700 text-white hover:from-teal-600 hover:to-teal-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-teal-500/30"><CheckCircle size={18} />{editingRecord ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {comparatifsEvolutionFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = comparatifsEvolutionFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setComparatifsEvolutionFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="comparatif-form" onSubmit={handleSubmit}>
                {comparatifsEvolutionFormTab === 'comparatifInfo' && (
                  <div className="space-y-6">
                  <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_title')}</label><input type="text" required value={formData.titre_comparatif} onChange={(e) => setFormData({ ...formData, titre_comparatif: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" placeholder={p('placeholder_title')} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_reference')}</label><input type="text" value={formData.comparatif_ref} onChange={(e) => setFormData({ ...formData, comparatif_ref: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" placeholder={p('placeholder_auto_generated')} /></div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_type')}</label><CustomSelect value={formData.type_comparatif} onChange={(value) => setFormData({ ...formData, type_comparatif: value })} options={typeSelectOptions} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_category')}</label><CustomSelect value={formData.categorie_analyse} onChange={(value) => setFormData({ ...formData, categorie_analyse: value })} options={categorySelectOptions} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_granularity')}</label><CustomSelect value={formData.granularite} onChange={(value) => setFormData({ ...formData, granularite: value })} options={granularitySelectOptions} searchable={false} /></div>
                          </div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_description')}</label><textarea value={formData.description_comparatif} onChange={(e) => setFormData({ ...formData, description_comparatif: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" placeholder={p('placeholder_description')} /></div>
                  </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <ComptabiliteFiscalYearField label={p('form_fiscal_year')} value={formData.exercice_fiscal} onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })} placeholder={p('placeholder_fiscal_year')} required />
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_ref_period_start')}</label><CustomDatePicker value={formData.periode_reference_debut} onChange={(dateString) => setFormData({ ...formData, periode_reference_debut: dateString })} placeholder={p('select')} /></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_comparison_period_start')}</label><CustomDatePicker value={formData.periode_comparaison_debut} onChange={(dateString) => setFormData({ ...formData, periode_comparaison_debut: dateString })} placeholder={p('select')} /></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {comparatifsEvolutionFormTab === 'analyseRecommandations' && (
<div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <p className="text-white/50 text-sm italic md:col-span-3">{p('section_gaps_placeholder')}</p>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_analysis_status')}</label><CustomSelect value={formData.statut_analyse} onChange={(value) => setFormData({ ...formData, statut_analyse: value })} options={statusSelectOptions} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_confidentiality')}</label><CustomSelect value={formData.confidentialite_niveau} onChange={(value) => setFormData({ ...formData, confidentialite_niveau: value })} options={confidentialitySelectOptions} searchable={false} /></div>
                        </div>
                        <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all" placeholder={p('placeholder_notes')} /></div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default ComparatifsEvolutionPage;

